import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Stack,
} from '@mui/material';
import { getStudentResultDetail, postSubmissionReevaluate, getSubmissionStatus } from '../../services/labResults.api';
import {
  ExpandBlock,
  JsonPreview,
  ScoreBadge,
  ScoreBreakdownTable,
  GitEvaluationTable,
  ApiEvaluationTable,
  UiEvaluationTable,
  EvaluatorFlagsBlock,
  FlagsRow,
  RemarksList,
  formatScore,
  normalizeEvaluatorSlices,
} from '../../components/labEvaluation/LabEvaluationSections';
import { formatDateTimeIST } from '../../utils/istDateTime';

function getSubmissionLabel(detail) {
  if (!detail || typeof detail !== 'object') return '';
  return String(
    detail.testName ||
      detail.labTitle ||
      detail.labName ||
      detail.title ||
      detail.name ||
      detail.labId ||
      ''
  ).trim();
}

function isExternalSubmission(detail) {
  return getSubmissionLabel(detail).toLowerCase().includes('external');
}

function getExecutionStatus(totalScore) {
  const score = Number(totalScore);
  if (!Number.isFinite(score)) return 'Not Executed';
  if (score > 75) return 'Executed';
  if (score >= 35) return 'Partially Executed';
  return 'Not Executed';
}

export default function StudentLabResultDetailPage() {
  const { submissionId } = useParams();
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reevalLoading, setReevalLoading] = useState(false);
  const [reevalMsg, setReevalMsg] = useState('');

  const load = async () => {
    if (!submissionId) return;
    setLoading(true);
    setError('');
    try {
      const d = await getStudentResultDetail(submissionId);
      setDetail(d);
      try {
        const s = await getSubmissionStatus(submissionId);
        setStatus(s?.status ?? d?.status ?? null);
      } catch {
        setStatus(d?.status ?? null);
      }
    } catch (e) {
      setDetail(null);
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when id changes
  }, [submissionId]);

  const handleReevaluate = async () => {
    if (!submissionId) return;
    setReevalLoading(true);
    setReevalMsg('');
    try {
      await postSubmissionReevaluate(submissionId);
      setReevalMsg('Re-evaluation queued. Status will move to PENDING, then PROCESSING.');
      setStatus('PENDING');
      await load();
    } catch (e) {
      setReevalMsg(e?.response?.data?.message || e?.message || 'Request failed');
    } finally {
      setReevalLoading(false);
    }
  };

  const flags = detail?.flags || {};
  const crit = useMemo(() => (Array.isArray(flags.critical) ? flags.critical : []), [flags.critical]);
  const warn = useMemo(() => (Array.isArray(flags.warning) ? flags.warning : []), [flags.warning]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
        <Alert severity="error">{error || 'Not found'}</Alert>
        <Button component={RouterLink} to="/student/lab-results" sx={{ mt: 2 }}>
          Back to list
        </Button>
      </Box>
    );
  }

  const breakdown = detail.breakdown || null;
  const breakdownGit = breakdown?.git || {};
  const breakdownApi = breakdown?.api || {};
  const breakdownUi = breakdown?.ui || {};

  const { git, api, ui } = normalizeEvaluatorSlices(detail);
  const externalSubmission = isExternalSubmission(detail);
  const executionStatus = getExecutionStatus(detail?.totalScore);

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      <Button component={RouterLink} to="/student/lab-results" size="small" sx={{ mb: 2 }}>
        ← My results
      </Button>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        Submission result
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Status: <strong>{status || detail.status || '—'}</strong>
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Button variant="outlined" size="small" onClick={handleReevaluate} disabled={reevalLoading}>
          {reevalLoading ? 'Queueing…' : 'Re-run evaluation'}
        </Button>
      </Box>
      {reevalMsg ? (
        <Alert severity={reevalMsg.includes('queued') ? 'success' : 'error'} sx={{ mb: 2 }}>
          {reevalMsg}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell width={180}>{externalSubmission ? 'Execution status' : 'Total score'}</TableCell>
              <TableCell>
                <strong>{externalSubmission ? executionStatus : formatScore(detail.totalScore)}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Grade</TableCell>
              <TableCell>
                <strong>{detail.grade ?? '—'}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Verdict</TableCell>
              <TableCell>{detail.verdict ?? '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Evaluated</TableCell>
              <TableCell>
                {formatDateTimeIST(detail.evaluatedAt, '—')}
              </TableCell>
            </TableRow>
            {detail.automatedEvaluation && !externalSubmission ? (
              <TableRow>
                <TableCell>Automated score</TableCell>
                <TableCell>
                  {formatScore(detail.automatedEvaluation.totalScore)} /{' '}
                  {detail.automatedEvaluation.grade ?? '—'}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      {detail.override ? (
        <ExpandBlock title="Faculty override" defaultOpen>
          <JsonPreview value={detail.override} maxHeight={200} />
        </ExpandBlock>
      ) : null}

      {!externalSubmission ? (
        <>
          <ExpandBlock title="Score breakdown" defaultOpen>
            <ScoreBreakdownTable breakdown={breakdown} />
          </ExpandBlock>

          <ExpandBlock
            title="Git evaluation"
            rightSlot={<ScoreBadge score={git.score ?? breakdownGit.score} maxScore={breakdownGit.maxScore} />}
          >
            <GitEvaluationTable git={git} />
            <EvaluatorFlagsBlock flags={git.flags} />
          </ExpandBlock>

          <ExpandBlock
            title="API test cases"
            rightSlot={<ScoreBadge score={api.score ?? breakdownApi.score} maxScore={breakdownApi.maxScore} />}
          >
            <ApiEvaluationTable api={api} />
            <EvaluatorFlagsBlock flags={api.flags} />
          </ExpandBlock>

          <ExpandBlock
            title="UI evaluation"
            rightSlot={<ScoreBadge score={ui.score ?? breakdownUi.score} maxScore={breakdownUi.maxScore} />}
          >
            <UiEvaluationTable ui={ui} />
            <EvaluatorFlagsBlock flags={ui.flags} />
          </ExpandBlock>
        </>
      ) : null}

      <ExpandBlock
        title="Flags & remarks"
        subtitle={`${crit.length} critical · ${warn.length} warning · ${Array.isArray(detail.remarks) ? detail.remarks.length : 0} remarks`}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Critical
            </Typography>
            <FlagsRow flags={crit} variant="critical" />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Warning
            </Typography>
            <FlagsRow flags={warn} variant="warning" />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Remarks
            </Typography>
            <RemarksList remarks={detail.remarks} />
          </Box>
        </Stack>
      </ExpandBlock>
    </Box>
  );
}
